import React, { useEffect, useState } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { Bone } from '../../../../components/ui/DashboardSkeletons';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FlagIcon from '@mui/icons-material/Flag';
import { API } from '../../../../api';
import type { IMemberProgram } from './MemberProgramsModal';
import { MOBILE_SX, useIsMobileApp } from '../../../../mobile/mobileApp';

// Shape from GET /elliott-assessment/{grpId}/{memberId} — Elliott's per-lesson
// assessment: 3-state flags (red / red-resolved / green), the Live Scenario
// section (own indicator + narrative) and the Quiz section (per-MCQ rows).
type FlagState = 'red' | 'redResolved' | 'green';
interface IAssessmentLesson {
  lessonId: string;
  lessonName: string;
  hasRisk: boolean;
  /** A-6 roll-up: red = unresolved, redResolved = 🔴→🟢, green = never flagged */
  lessonState?: FlagState;
  /** A-7 Live Scenario section — null when the lesson has no scenario */
  scenario?: { state: FlagState; assessment: string } | null;
  /**
   * A-7 Quiz section — one row per MCQ the member ACTUALLY answered (from their
   * live-sim checks or legacy answers). Questions added to the lesson after they
   * completed it, and questions never attempted, are excluded by the BE.
   */
  quiz?: Array<{ questionId: string; question: string; correct: boolean; attempts: number }>;
  assessment: string;
}
interface IAssessmentModule {
  moduleId: string;
  moduleName: string;
  lessons: IAssessmentLesson[];
}
interface IAssessmentReport {
  memberId: string;
  programId: string;
  programName: string;
  mcqRetakeMode?: boolean;
  modules: IAssessmentModule[];
}

interface ReportState {
  loading: boolean;
  error: boolean;
  report: IAssessmentReport | null;
}

interface MemberRiskAreasModalProps {
  open: boolean;
  onClose: () => void;
  memberName: string;
  memberId: string;
  programs: IMemberProgram[];
}

const RED = '#FF6A55';

const sectionLabelSx = {
  fontSize: 12,
  fontWeight: 700,
  color: '#B6B6B6',
  fontFamily: 'Inter, sans-serif',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.4px'
};

const mutedSx = { fontSize: 13, color: '#868686', fontFamily: 'Inter, sans-serif' };

const lessonStateOf = (lesson: IAssessmentLesson): FlagState =>
  lesson.lessonState ?? (lesson.hasRisk ? 'red' : 'green');

/**
 * Risk Areas — the lessons behind a member's red-flag count on Org Members. The
 * same Elliott's Assessment breakdown the admin used to reach by expanding a
 * program under View Report, cut down to what is still red: flagged modules,
 * their unresolved lessons, and within each lesson only the half that flagged
 * it — a failed Live Scenario, or (in quiz-retake programs) the questions still
 * answered wrong. Resolved and never-flagged lessons are left out.
 */
const MemberRiskAreasModal = ({ open, onClose, memberName, memberId, programs }: MemberRiskAreasModalProps) => {
  // A phone: the whole screen.
  const isMobileApp = useIsMobileApp();
  const [reports, setReports] = useState<Record<string, ReportState>>({});
  // Flagged modules start open — these lessons are what the admin came for.
  const [collapsedModules, setCollapsedModules] = useState<Set<string>>(new Set());

  // Fetch the programs the list payload says are flagged — or all of them if
  // none are, since the per-program counts are absent when the BE's stats step
  // fails while the member-level count can still be set.
  const flagged = programs.filter((p) => (p.unresolvedFlags ?? 0) > 0);
  const targets = flagged.length > 0 ? flagged : programs;
  const targetKey = targets.map((p) => p.id).join(',');

  useEffect(() => {
    if (!open || !memberId) return;
    let cancelled = false;
    const initial: Record<string, ReportState> = {};
    targets.forEach((p) => {
      initial[p.id] = { loading: true, error: false, report: null };
    });
    setReports(initial);
    setCollapsedModules(new Set());

    const loadReport = async (programId: string) => {
      let next: ReportState;
      try {
        const res = await API.getElliottAssessment({ groupId: programId, memberId });
        next = { loading: false, error: false, report: res.data as IAssessmentReport };
      } catch {
        next = { loading: false, error: true, report: null };
      }
      if (!cancelled) setReports((prev) => ({ ...prev, [programId]: next }));
    };
    targets.forEach((p) => {
      loadReport(p.id);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, memberId, targetKey]);

  const toggleModule = (key: string) =>
    setCollapsedModules((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const sections = targets.map((program) => {
    const state = reports[program.id];
    const modules = (state?.report?.modules || [])
      .map((mod) => ({
        mod,
        // Numbered by position in the module, so "Lesson 3" is still the module's third lesson.
        lessons: mod.lessons
          .map((lesson, index) => ({ lesson, number: index + 1 }))
          .filter(({ lesson }) => lessonStateOf(lesson) === 'red')
      }))
      .filter((m) => m.lessons.length > 0);
    return { program, state, modules };
  });
  const settled = sections.every((s) => s.state && !s.state.loading);
  const anyError = sections.some((s) => s.state?.error);
  const anyRed = sections.some((s) => s.modules.length > 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isMobileApp}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.60)', backdropFilter: 'blur(10.95px)' } }
      }}
      PaperProps={{
        sx: {
          width: 760,
          maxWidth: '95vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#0F0F0F',
          borderRadius: '8px',
          outline: '1px solid #444444',
          outlineOffset: '-1px',
          overflow: 'hidden',
          boxShadow: 'none',
          [MOBILE_SX]: {
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            maxHeight: '100%',
            m: 0,
            borderRadius: 0,
            outline: 'none'
          }
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          pl: '25px',
          pr: '45px',
          py: '20px',
          borderBottom: '1px solid #2A2A2A',
          position: 'relative',
          [MOBILE_SX]: { pl: '16px', pr: '52px', py: '14px' }
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#FDFDFD', fontFamily: 'Inter, sans-serif' }}>
            {memberName}&rsquo;s Learning Gaps
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#868686', fontFamily: 'Inter, sans-serif', mt: '2px' }}>
            Lessons with an unresolved red flag. Each one clears when it&rsquo;s resolved through Improve Performance.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            p: 0.5,
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            [MOBILE_SX]: { p: '10px', right: 6 }
          }}
        >
          <CloseIcon sx={{ fontSize: 20, color: '#FDFDFD' }} />
        </IconButton>
      </Box>

      {/* Body (scrollable) */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: '25px', py: '20px', [MOBILE_SX]: { px: '16px' } }}>
        {sections.map(({ program, state, modules }) => {
          if (!state || state.loading) {
            return (
              <Box
                key={program.id}
                aria-busy
                aria-label={`Loading ${program.name}`}
                sx={{ display: 'flex', flexDirection: 'column', gap: '8px', py: '10px' }}
              >
                <Bone width="45%" height={18} />
                <Bone height={40} />
              </Box>
            );
          }
          if (state.error) {
            return (
              <Typography key={program.id} sx={{ ...mutedSx, py: '10px' }}>
                Elliott&rsquo;s assessment for {program.name} could not be loaded. Refresh the page and try again.
              </Typography>
            );
          }
          if (modules.length === 0) return null;
          const programCount = modules.reduce((n, m) => n + m.lessons.length, 0);
          const retakeMode = Boolean(state.report?.mcqRetakeMode);
          return (
            <Box key={program.id} sx={{ '&:not(:last-of-type)': { mb: '20px' } }}>
              {/* Program heading */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  pb: '8px',
                  mb: '6px',
                  borderBottom: '1px solid #2A2A2A'
                }}
              >
                <Typography
                  sx={{ fontSize: 14, fontWeight: 700, color: '#FDFDFD', fontFamily: 'Inter, sans-serif', flex: 1 }}
                >
                  {program.name}
                </Typography>
                <FlagIcon sx={{ fontSize: 15, color: RED }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: RED, fontFamily: 'Inter, sans-serif' }}>
                  {programCount}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {modules.map(({ mod, lessons }) => {
                  const modKey = `${program.id}-${mod.moduleId}`;
                  const modOpen = !collapsedModules.has(modKey);
                  return (
                    <Box key={mod.moduleId} sx={{ borderLeft: '1px solid #2A2A2A', pl: '12px' }}>
                      {/* Module header */}
                      <Box
                        onClick={() => toggleModule(modKey)}
                        sx={{ display: 'flex', alignItems: 'center', gap: '8px', py: '8px', cursor: 'pointer' }}
                      >
                        <FlagIcon sx={{ fontSize: 15, color: RED, flexShrink: 0 }} />
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: RED,
                            fontFamily: 'Inter, sans-serif',
                            flexShrink: 0
                          }}
                        >
                          {lessons.length}
                        </Typography>
                        <Box sx={{ display: 'flex', color: '#868686', flexShrink: 0 }}>
                          {modOpen ? (
                            <ExpandLessIcon sx={{ fontSize: 20 }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: 20 }} />
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#FDFDFD',
                            fontFamily: 'Inter, sans-serif',
                            flex: 1
                          }}
                        >
                          {mod.moduleName}
                        </Typography>
                      </Box>

                      {/* Unresolved lessons — only the section(s) that flagged each one */}
                      {modOpen && (
                        <Box
                          sx={{
                            ml: '16px',
                            mt: '2px',
                            mb: '12px',
                            pl: '14px',
                            pr: '12px',
                            py: '12px',
                            border: '1px solid #2A2A2A',
                            borderRadius: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px'
                          }}
                        >
                          {lessons.map(({ lesson, number }) => {
                            // Older payloads have no `scenario` section — the flat
                            // narrative is the scenario's, and it's what flagged the lesson.
                            const scenario =
                              lesson.scenario !== undefined
                                ? lesson.scenario
                                : { state: 'red' as FlagState, assessment: lesson.assessment };
                            const scenarioRed = scenario?.state === 'red';
                            // Wrong answers only flag a lesson in quiz-retake programs;
                            // elsewhere they aren't red flags, so they stay out.
                            const wrongQuestions = retakeMode ? (lesson.quiz || []).filter((q) => !q.correct) : [];
                            return (
                              <Box key={lesson.lessonId}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '6px' }}>
                                  <FlagIcon sx={{ fontSize: 14, color: RED, flexShrink: 0 }} />
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      fontWeight: 600,
                                      color: '#FDFDFD',
                                      fontFamily: 'Inter, sans-serif'
                                    }}
                                  >
                                    {`Lesson ${number}: ${lesson.lessonName}`}
                                  </Typography>
                                </Box>

                                {scenarioRed && (
                                  <Box sx={{ pl: '22px', mb: wrongQuestions.length ? '10px' : 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '3px' }}>
                                      <FlagIcon sx={{ fontSize: 12, color: RED, flexShrink: 0 }} />
                                      <Typography sx={sectionLabelSx}>Roleplay</Typography>
                                    </Box>
                                    <Typography
                                      sx={{
                                        fontSize: 13,
                                        color: '#B6B6B6',
                                        fontFamily: 'Inter, sans-serif',
                                        lineHeight: '20px'
                                      }}
                                    >
                                      {scenario?.assessment ||
                                        'Elliott didn’t record a written assessment for this attempt.'}
                                    </Typography>
                                  </Box>
                                )}

                                {wrongQuestions.length > 0 && (
                                  <Box sx={{ pl: '22px' }}>
                                    <Typography sx={{ ...sectionLabelSx, mb: '4px' }}>Quiz</Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      {wrongQuestions.map((q) => (
                                        <Box
                                          key={q.questionId}
                                          sx={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: 12.5,
                                              fontWeight: 700,
                                              color: RED,
                                              fontFamily: 'Inter, sans-serif',
                                              width: 12,
                                              flexShrink: 0
                                            }}
                                          >
                                            ✗
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: 12.5,
                                              color: '#B6B6B6',
                                              fontFamily: 'Inter, sans-serif',
                                              lineHeight: '18px',
                                              flex: 1
                                            }}
                                          >
                                            {q.question}
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: 12,
                                              color: '#868686',
                                              fontFamily: 'Inter, sans-serif',
                                              flexShrink: 0
                                            }}
                                          >
                                            {q.attempts === 1 ? '1 attempt' : `${q.attempts} attempts`}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}

        {settled && !anyRed && !anyError && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <Typography sx={{ ...mutedSx, fontSize: 14 }}>
              {memberName} has no unresolved red flags right now.
            </Typography>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default MemberRiskAreasModal;
